import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { LogIn, Mail, Lock } from "lucide-react";

const CustomerLoginPage = () => {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const from = location.state?.from || "/account";

    useEffect(() => {
        if (user) navigate(from, { replace: true });
    }, [user, navigate, from]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Invalid email or password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[60vh] flex items-center justify-center py-16 px-6" data-testid="customer-login-page">
            <Card className="w-full max-w-md rounded-none border-border">
                <CardContent className="p-8">
                    <div className="text-center mb-8">
                        <div className="w-12 h-12 bg-primary flex items-center justify-center mx-auto mb-4">
                            <LogIn className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h1 className="font-heading font-black text-2xl tracking-tight">SIGN IN</h1>
                        <p className="text-muted-foreground text-sm mt-1">Access your order history and account</p>
                    </div>

                    {error && (
                        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm p-3 mb-6" data-testid="login-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="pl-10 rounded-none"
                                    placeholder="your@email.com"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="pl-10 rounded-none"
                                    placeholder="Your password"
                                    required
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded-none uppercase font-bold tracking-wider"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? "Signing in..." : "Sign In"}
                        </Button>
                    </form>

                    <div className="text-center mt-6 text-sm text-muted-foreground">
                        Don't have an account?{" "}
                        <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                            Create one
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default CustomerLoginPage;
